import { useCallback } from "react";
import { FACTOR_90, FACTOR_75 } from "../constants";

export const useVPPipelineCalcs = ({
  oportunidades,
  oportunidadesOtroPais,
  showInDollars,
  modoCombinado,
  tipoCambioActual,
  pais,
}) => {
  // Helper: filtra ops por mes_cierre y agrupa por cliente+país tomando mínimo
  const getMinByClientAndPais = useCallback(
    (ops, estatus) => {
      const filtered = ops.filter((o) => o.estatus === estatus);
      const byKey = {};
      filtered.forEach((o) => {
        const key = `${o.client_id || o.cliente}-${o.paisOrigen}`;
        const importe = parseFloat(o.importe) || 0;
        if (!byKey[key] || importe < byKey[key].importe) {
          byKey[key] = { importe, paisOrigen: o.paisOrigen };
        }
      });
      return Object.values(byKey).reduce((sum, item) => {
        if (showInDollars || modoCombinado) {
          const rate = item.paisOrigen === "Chile" ? tipoCambioActual : 3.75;
          return sum + item.importe / rate;
        }
        return sum + item.importe;
      }, 0);
    },
    [showInDollars, modoCombinado, tipoCambioActual]
  );

  // Helper: construye array de ops filtradas por un campo de mes dado
  const buildFilteredOps = useCallback(
    (monthIndex, campoMes) => {
      const filterByMonth = (ops) =>
        ops
          .filter((o) => o.estatus === "90%" || o.estatus === "75%")
          .filter((o) => !o.eliminada && o[campoMes])
          .filter((o) => {
            const [mesStr, anoStr] = o[campoMes].split("-");
            return Number(anoStr) === 2026 && Number(mesStr) === monthIndex + 1;
          });

      let allOps = filterByMonth(oportunidades).map((o) => ({ ...o, paisOrigen: pais }));

      if (modoCombinado && oportunidadesOtroPais.length > 0) {
        const otroPais = pais === "Chile" ? "Perú" : "Chile";
        const opsOtro = filterByMonth(oportunidadesOtroPais).map((o) => ({ ...o, paisOrigen: otroPais }));
        allOps = [...allOps, ...opsOtro];
      }

      return allOps;
    },
    [oportunidades, oportunidadesOtroPais, modoCombinado, pais]
  );

  // Pipeline ponderado por mes_cierre (para forecast de cierres)
  const calcularPipeline = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_cierre");
      const sum90 = getMinByClientAndPais(allOps, "90%") * FACTOR_90;
      const sum75 = getMinByClientAndPais(allOps, "75%") * FACTOR_75;
      return sum90 + sum75;
    },
    [buildFilteredOps, getMinByClientAndPais]
  );

  const calcularPipelineTotal = useCallback(() => {
    let total = 0;
    for (let i = 0; i < 12; i++) total += calcularPipeline(i);
    return total;
  }, [calcularPipeline]);

  // Pipeline BRUTO (sin factores) por mes_cierre — para columna de tabla
  const calcularPipelineBruto = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_cierre");
      const sum90 = getMinByClientAndPais(allOps, "90%");
      const sum75 = getMinByClientAndPais(allOps, "75%");
      return sum90 + sum75;
    },
    [buildFilteredOps, getMinByClientAndPais]
  );

  const calcularPipelineBrutoTotal = useCallback(() => {
    let total = 0;
    for (let i = 0; i < 12; i++) total += calcularPipelineBruto(i);
    return total;
  }, [calcularPipelineBruto]);

  // Pipeline ponderado por mes_factura (para forecast de facturación)
  const calcularPipelineFacturacion = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_factura");
      const sum90 = getMinByClientAndPais(allOps, "90%") * FACTOR_90;
      const sum75 = getMinByClientAndPais(allOps, "75%") * FACTOR_75;
      return sum90 + sum75;
    },
    [buildFilteredOps, getMinByClientAndPais]
  );

  const getPipelineItems = useCallback(
    (monthIndex, isTotal = false) => {
      const filterByMonth = (ops, month) =>
        ops.filter((o) => {
          if (!o.mes_cierre) return false;
          const [mesStr, anoStr] = o.mes_cierre.split("-");
          if (isTotal) return Number(anoStr) === 2026;
          return Number(anoStr) === 2026 && Number(mesStr) === month + 1;
        });

      const ops90Local = filterByMonth(
        oportunidades.filter((o) => o.estatus === "90%" && !o.eliminada),
        monthIndex
      );
      const ops75Local = filterByMonth(
        oportunidades.filter((o) => o.estatus === "75%" && !o.eliminada),
        monthIndex
      );

      let allOps = [
        ...ops90Local.map((o) => ({ ...o, paisOrigen: pais })),
        ...ops75Local.map((o) => ({ ...o, paisOrigen: pais })),
      ];

      if (modoCombinado && oportunidadesOtroPais.length > 0) {
        const otroPais = pais === "Chile" ? "Perú" : "Chile";
        const ops90Otro = filterByMonth(
          oportunidadesOtroPais.filter((o) => o.estatus === "90%" && !o.eliminada),
          monthIndex
        );
        const ops75Otro = filterByMonth(
          oportunidadesOtroPais.filter((o) => o.estatus === "75%" && !o.eliminada),
          monthIndex
        );
        allOps = [
          ...allOps,
          ...ops90Otro.map((o) => ({ ...o, paisOrigen: otroPais })),
          ...ops75Otro.map((o) => ({ ...o, paisOrigen: otroPais })),
        ];
      }

      // Deduplicación: para mismo client_id + país + estatus, tomar solo la de menor valor
      const byClientAndPais = {};
      allOps.forEach((o) => {
        const key = `${o.client_id || o.cliente}-${o.paisOrigen}-${o.estatus}`;
        const importe = parseFloat(o.importe) || 0;
        if (!byClientAndPais[key] || importe < byClientAndPais[key].importe) {
          byClientAndPais[key] = { ...o, importe };
        }
      });

      return Object.values(byClientAndPais);
    },
    [oportunidades, oportunidadesOtroPais, pais, modoCombinado]
  );

  return {
    calcularPipeline,
    calcularPipelineTotal,
    calcularPipelineBruto,
    calcularPipelineBrutoTotal,
    calcularPipelineFacturacion,
    getPipelineItems,
  };
};
